<script setup lang="ts">
import { storeToRefs } from "pinia";
import { onMounted, computed, ref } from "vue";

import { EDashboardType } from "@/app/data/dashboard/dashboard.types";
import { IPodiumItem } from "@/components/data/dashboard/insights-podium/insights-podium.types";
import { TCategorizedDataResponseSerie } from "@/components/ui/chart/types/categorized-chart-series.type";
import { TTemporalDataResponseSerie } from "@/components/ui/chart/types/temporal-chart-series.type";
import { useGetHeatmapQuery } from "@/composables/kili/consumption/queries/use-get-heatmap-query";
import { useGetMultisiteConsumptionMissingInfosQuery } from "@/composables/kili/consumption/queries/use-get-multisite-consumption-missing-infos-query";
import { useGetMultisiteConsumptionQuery } from "@/composables/kili/consumption/queries/use-get-multisite-consumption-query";
import { useGetSitesQuery } from "@/composables/kili/sites/queries/use-get-sites-query";
import { DATA_TEST_IDS } from "@/constants/data-test-ids/data-test-ids.constant";
import { EDataUnit } from "@/constants/data-units.type";
import { EMainConsumptionSource } from "@/data/api/kili/consumption/types/common-consumption.type";
import {
  ECurveConsumption,
  EColumnM2Consumption,
  EColumnPeriodComparisonConsumption,
  EYearConsumption,
} from "@/data/api/kili/consumption/types/consumption-metrics.type";
import {
  TMultisiteConsumptionRequest,
  TSiteIdsQuery,
} from "@/data/api/kili/consumption/types/multisite-consumption.type";
import { TDateRange } from "@/data/api/kili/consumption/types/temporal-metrics.type";
import { keyOf, useTypedIntl } from "@/locales/typed-intl";
import { EMixpanelEvent } from "@/services/mixpanel/mixpanel-events.type";
import { MixpanelService } from "@/services/mixpanel/mixpanel-service";
import { useDashboardStore } from "@/stores/dashboard/dashboard-store";
import { chain } from "@/utils/chain";
import $date from "@/utils/date";
import { isDefined } from "@/utils/object";
import { formatWhValue } from "@/utils/translation-utils";

import AreaConsumptionChart from "@/app/data/dashboard/cards/common/area-consumption-chart.vue";
import ConsumptionComparisonChart from "@/app/data/dashboard/cards/common/consumption-comparison-chart.vue";
import DeviceConsumptionDistributionChart from "@/app/data/dashboard/cards/common/device-consumption-distribution-chart.vue";
import HeatmapConsumptionChart from "@/app/data/dashboard/cards/common/heatmap-consumption-chart.vue";
import PodiumCard from "@/app/data/dashboard/cards/common/podium-card.vue";
import TemporalConsumptionChart from "@/app/data/dashboard/cards/common/temporal-consumption-chart.vue";
import InsightsMulti from "@/app/data/dashboard/cards/multi/insights-multi.vue";
import TopFlopConsumptionCardsMulti from "@/app/data/dashboard/cards/multi/top-flop-consumption-cards-multi.vue";
import MissingDsoSubscriptionDialog from "@/app/data/dashboard/dialog/missing-dso-subscription-dialog.vue";
import MissingSurfaceAreaMultiDialog from "@/app/data/dashboard/dialog/missing-surface-area-multi-dialog.vue";
import ContactVoltalisButton from "@/components/contact/contact-voltalis-button.vue";

interface IProps {
  fromDay: Date;
  toDay: Date;
}

const props = defineProps<IProps>();

const MULTI = 2;
const { t, k } = useTypedIntl();

const dashboardStore = useDashboardStore();
const { sitePerimeter } = storeToRefs(dashboardStore);

const dateRange: TDateRange = {
  dateRange: {
    from: $date.formatLocalDate(props.fromDay),
    to: $date.formatLocalDate(props.toDay),
  },
};

const { data: sites } = useGetSitesQuery();

const allSitesIds = computed<number[]>(() => {
  return sites.value?.map((s) => s.siteId) ?? [];
});

const selectedSiteIds = computed<TSiteIdsQuery>(() => {
  return sitePerimeter.value?.map((s) => s.siteId) ?? [];
});

const consumptionRequest = computed<TMultisiteConsumptionRequest>(() => {
  return {
    ...dateRange,
    siteIds: selectedSiteIds.value.length ? selectedSiteIds.value : undefined,
  };
});

const heatmapSiteIds = computed<TSiteIdsQuery>(() => {
  return selectedSiteIds.value.length ? selectedSiteIds.value : allSitesIds.value;
});

const { data: consumption, isPending: isConsumptionPending } = useGetMultisiteConsumptionQuery(consumptionRequest);

const { data: missingInfos } = useGetMultisiteConsumptionMissingInfosQuery(selectedSiteIds);

const { data: heatmapConsumption, isPending: isHeatmapConsumptionPending } = useGetHeatmapQuery(heatmapSiteIds);

const isMissingSurfaceAreaDialogVisible = ref<boolean>(false);
const isMissingDsoSubscriptionDialogVisible = ref<boolean>();

const hasNoDsoDataAndNoInstalledEquipments = computed<boolean>(() => {
  return !consumption.value?.global.value && !consumption.value?.devicesCount;
});

const podiumItems = computed<IPodiumItem[]>(() => {
  if (!consumption.value?.bySite) return [];

  return chain(consumption.value.bySite)
    .filterMap((siteConsumption) => {
      if (!siteConsumption.consumption.value) {
        return undefined;
      }

      return {
        tooltip: formatWhValue(siteConsumption.consumption.value),
        name: siteConsumption.name,
        value: siteConsumption.consumption.value,
      };
    })
    .sortBy((consumption) => consumption.value)
    .slice(0, 3)
    .value();
});

const isMissingDsoSubscriptionWarningVisible = computed<boolean>(() => {
  return (
    !!missingInfos.value?.missingSomeDsoIndexSubscriptions || !!missingInfos.value?.missingAllDsoIndexSubscriptions
  );
});

const formattedConsumptionCurvesSeries = computed<TTemporalDataResponseSerie<ECurveConsumption>[]>(() => {
  const series: TTemporalDataResponseSerie<ECurveConsumption>[] = [];

  if (consumption.value?.devicesByDay) {
    series.push({
      category: ECurveConsumption.DEVICE_CONSUMPTION_DETAILED_CURVE,
      value: consumption.value.devicesByDay.value,
      unit: consumption.value.devicesByDay.unit,
    });
  }

  if (consumption.value?.globalByDay.value.length) {
    series.push({
      category: ECurveConsumption.DSO_CONSUMPTION_DETAILED_CURVE,
      value: consumption.value.globalByDay.value,
      unit: consumption.value.globalByDay.unit,
    });
  }

  return series;
});

const formattedConsumptionAreaBySiteSeries = computed<
  TCategorizedDataResponseSerie<EColumnM2Consumption>[] | undefined
>(() => {
  if (!consumption.value) return;

  const orderedSitesWithConsumptionPerArea = chain(consumption.value.bySite)
    .filterMap((siteConsumption) => {
      const value = siteConsumption.consumptionPerArea.value;
      if (typeof value !== "number") return undefined;

      return {
        ...siteConsumption,
        consumptionPerArea: {
          ...siteConsumption.consumptionPerArea,
          value,
        },
      };
    })
    .sortBy((a) => -a.consumptionPerArea.value)
    .value();

  const sitesWithoutConsumptionPerArea = consumption.value.bySite.filter(
    (siteConsumption) => typeof siteConsumption.consumptionPerArea.value !== "number",
  );

  const allOrderedSites = [...orderedSitesWithConsumptionPerArea, ...sitesWithoutConsumptionPerArea];

  return [
    {
      kind: EColumnM2Consumption.AREA_CONSUMPTION,
      unit: EDataUnit.WATT_HOUR,
      value: allOrderedSites.map((siteConsumption) => {
        return {
          name: siteConsumption.name,
          value: siteConsumption.consumptionPerArea.value,
        };
      }),
    },
  ];
});

const isMissingAllConsumptionArea = computed<boolean>(() => {
  if (!consumption.value) return false;

  return consumption.value.bySite.every((siteConsumption) => !siteConsumption.consumptionPerArea.value);
});

const formattedConsumptionComparisonSeries = computed<
  TCategorizedDataResponseSerie<EColumnPeriodComparisonConsumption>[]
>(() => {
  if (!consumption.value?.bySite) return [];

  const orderedSitesWithConsumption = chain(consumption.value.bySite)
    .filterMap((siteConsumption) => {
      const value = siteConsumption.consumption.value;
      if (typeof value !== "number") return undefined;

      return {
        ...siteConsumption,
        consumption: {
          ...siteConsumption.consumption,
          value,
        },
      };
    })
    .sortBy((a) => -a.consumption.value)
    .value();

  const sitesWithoutConsumption = consumption.value.bySite.filter(
    (siteConsumption) => typeof siteConsumption.consumption.value !== "number",
  );

  const allOrderedSites = [...orderedSitesWithConsumption, ...sitesWithoutConsumption];

  return [
    {
      kind: EColumnPeriodComparisonConsumption.LAST_PERIOD_CONSUMPTION,
      unit: EDataUnit.WATT_HOUR,
      value: allOrderedSites.map((siteConsumption) => {
        return {
          name: siteConsumption.name,
          value: siteConsumption.lastPeriodConsumption.value,
        };
      }),
    },
    {
      kind: EColumnPeriodComparisonConsumption.CURRENT_PERIOD_CONSUMPTION,
      unit: EDataUnit.WATT_HOUR,
      value: allOrderedSites.map((siteConsumption) => {
        return {
          name: siteConsumption.name,
          value: siteConsumption.consumption.value,
        };
      }),
    },
  ];
});

const formattedYearConsumptionSerie = computed<TTemporalDataResponseSerie<EYearConsumption> | undefined>(() => {
  if (!heatmapConsumption.value) return;

  return {
    unit: EDataUnit.WATT_HOUR,
    category: EYearConsumption.YEAR_CONSUMPTION,
    value: heatmapConsumption.value.heatmap,
  };
});

onMounted(() => {
  MixpanelService.trackEvent(EMixpanelEvent.DASHBOARD_MULTI);
});
</script>

<template>
  <div
    class="flex flex-col gap-6 pb-8"
    :data-test-id="DATA_TEST_IDS.views.dashboard.multisite"
  >
    <div class="2xl:grid grid-flow-col grid-cols-3 max-2xl:flex max-2xl:flex-col gap-6">
      <InsightsMulti
        class="col-span-2"
        :consumption="consumption"
        :is-consumption-loading="isConsumptionPending"
        :is-missing-dso-subscription-warning-visible="isMissingDsoSubscriptionWarningVisible"
        :missing-infos="missingInfos"
        @open-missing-subscription-dialog="isMissingDsoSubscriptionDialogVisible = true"
        @open-missing-surface-areas-dialog="isMissingSurfaceAreaDialogVisible = true"
      />

      <PodiumCard
        :data-test-id="DATA_TEST_IDS.views.dashboard.podiums.lessEnergyConsummingPodium"
        :is-loading="isConsumptionPending"
        :items="podiumItems"
        :type="EDashboardType.MULTISITE"
      />
    </div>

    <div class="grid grid-flow-row xl:grid-cols-2 max-xl:grid-cols-1 gap-6">
      <TemporalConsumptionChart
        :data-test-id="DATA_TEST_IDS.views.dashboard.charts.consumptionEvolutionChart"
        :from-day="props.fromDay"
        :has-no-data="hasNoDsoDataAndNoInstalledEquipments"
        :is-loading="isConsumptionPending"
        :series="formattedConsumptionCurvesSeries"
        :to-day="props.toDay"
        :type="EDashboardType.MULTISITE"
      >
        <template #noDataMessage>
          <div class="flex flex-col gap-4">
            <h5>{{ t(k.data.dashboard.noDataChartMessage.title) }}</h5>
            <i18n-t
              :keypath="keyOf(k.data.dashboard.noDataChartMessage.noInstalledDevices)"
              tag="p"
            >
              <template #installedDevices>
                <b>
                  {{ t(k.data.dashboard.noDataChartMessage.installedDevices) }}
                </b>
              </template>
            </i18n-t>
            <ContactVoltalisButton :data-test-id="DATA_TEST_IDS.views.dashboard.buttons.contactVoltalisBtn" />
          </div>
        </template>
      </TemporalConsumptionChart>

      <AreaConsumptionChart
        :data-test-id="DATA_TEST_IDS.views.dashboard.charts.consumptionPerM2Chart"
        :has-no-data="isMissingAllConsumptionArea"
        :is-loading="isConsumptionPending"
        :series="formattedConsumptionAreaBySiteSeries"
        :type="EDashboardType.MULTISITE"
      >
        <template #header-right>
          <div
            v-if="missingInfos?.missingAreaSites.length"
            class="flex gap-2 items-center bg-warning-soft text-warning-700 px-1 cursor-pointer rounded-full"
            @click="isMissingSurfaceAreaDialogVisible = true"
          >
            <i class="pi pi-info-circle text-xs" />
            <small>{{
              t(k.data.dashboard.insights.m2Consumption.missingSurfaceArea({ count: sites?.length ?? MULTI }))
            }}</small>
          </div>
        </template>

        <template #noDataMessage>
          <div class="flex flex-col gap-4">
            <h5>{{ t(k.data.dashboard.noDataChartMessage.title) }}</h5>
            <p>{{ t(k.data.dashboard.missingSurfaceArea.subtitle.total[EDashboardType.MULTISITE]) }}</p>
          </div>
        </template>
      </AreaConsumptionChart>
    </div>

    <div class="grid grid-flow-row xl:grid-cols-5 max-xl:grid-cols-1 gap-6">
      <ConsumptionComparisonChart
        class="xl:col-span-3"
        :data-test-id="DATA_TEST_IDS.views.dashboard.charts.consumptionComparisonChart"
        :has-no-data="hasNoDsoDataAndNoInstalledEquipments"
        :is-loading="isConsumptionPending"
        :series="formattedConsumptionComparisonSeries"
        :source="consumption?.mainConsumptionSource"
        :type="EDashboardType.MULTISITE"
      >
        <template #header-right>
          <div
            v-if="
              isMissingDsoSubscriptionWarningVisible &&
              consumption?.mainConsumptionSource === EMainConsumptionSource.DSO
            "
            class="flex gap-2 items-center bg-warning-soft text-warning-700 px-1 cursor-pointer rounded-full"
            @click="isMissingDsoSubscriptionDialogVisible = true"
          >
            <i class="pi pi-info-circle text-xs" />
            <small>{{ t(k.data.dashboard.insights.totalConsumption.missingDsoSubscription) }}</small>
          </div>
        </template>

        <template #noDataMessage>
          <div class="flex flex-col gap-4">
            <h5>{{ t(k.data.dashboard.noDataChartMessage.title) }}</h5>
            <i18n-t
              :keypath="keyOf(k.data.dashboard.noDataChartMessage.noInstalledDevices)"
              tag="p"
            >
              <template #installedDevices>
                <b>
                  {{ t(k.data.dashboard.noDataChartMessage.installedDevices) }}
                </b>
              </template>
            </i18n-t>

            <ContactVoltalisButton :data-test-id="DATA_TEST_IDS.views.dashboard.buttons.contactVoltalisBtn" />
          </div>
        </template>
      </ConsumptionComparisonChart>

      <DeviceConsumptionDistributionChart
        class="xl:col-span-2"
        :consumption="consumption"
        :data-test-id="DATA_TEST_IDS.views.dashboard.charts.consumptionDistributionPerDeviceTypesChart"
        :has-no-data="!consumption?.devicesCount"
        :is-loading="isConsumptionPending"
        :type="EDashboardType.MULTISITE"
      >
        <template #noDataMessage>
          <div class="flex flex-col gap-4">
            <h5>{{ t(k.data.dashboard.noDataChartMessage.title) }}</h5>
            <i18n-t
              :keypath="keyOf(k.data.dashboard.noDataChartMessage.noInstalledDevices)"
              tag="p"
            >
              <template #installedDevices>
                <b>
                  {{ t(k.data.dashboard.noDataChartMessage.installedDevices) }}
                </b>
              </template>
            </i18n-t>
            <ContactVoltalisButton :data-test-id="DATA_TEST_IDS.views.dashboard.buttons.contactVoltalisBtn" />
          </div>
        </template>
      </DeviceConsumptionDistributionChart>
    </div>

    <TopFlopConsumptionCardsMulti
      :consumption="consumption"
      :is-loading="isConsumptionPending"
    />

    <HeatmapConsumptionChart
      :data-test-id="DATA_TEST_IDS.views.dashboard.charts.consumptionHeatmap"
      :is-loading="isHeatmapConsumptionPending"
      :serie="formattedYearConsumptionSerie"
      :title="
        isDefined(heatmapConsumption?.source)
          ? t(
              k.data.dashboard.consumptionHeatmap.title[heatmapConsumption.source]({
                count: sites?.length ?? MULTI,
              }),
            )
          : t(k.data.dashboard.consumptionHeatmap.title.fallback)
      "
      :type="EDashboardType.MULTISITE"
    />
  </div>

  <MissingSurfaceAreaMultiDialog
    v-model:visible="isMissingSurfaceAreaDialogVisible"
    :data-test-id="DATA_TEST_IDS.views.dashboard.missingInfos.multi.missingSurfaceArea.dialog"
    :missing-infos="missingInfos"
    :no-data="isMissingAllConsumptionArea"
  />

  <MissingDsoSubscriptionDialog
    v-model:visible="isMissingDsoSubscriptionDialogVisible"
    :data-test-id="DATA_TEST_IDS.views.dashboard.missingInfos.missingDsoSubscription.dialog"
    :type="EDashboardType.MULTISITE"
  />
</template>
